/*
 * The platform's own module permissions.
 *
 * This file used to enumerate every module in the product (`MODULES.QUOTING`,
 * `PERMISSIONS.QUOTING_USE`). It no longer does: modules declare their own vocabulary
 * via `defineModulePermissions`, so the shared contract package no longer needs to know
 * which modules exist. What remains is the Admin section, which is platform chrome
 * rather than a module package.
 *
 * It lives HERE rather than in apps/server because both apps need it — the server to
 * seed and guard, apps/web to render nav and the access UI — and apps cannot import each
 * other. (The plan said apps/server; this is the correction.)
 *
 * The old single `admin:access` is split. "Can see who has access" and "can change it"
 * are genuinely different, and Access Control is the first screen that wants the
 * distinction: a lead can be allowed to audit permissions without being able to grant
 * themselves more.
 */
import { defineModulePermissions } from './spec';

export const ADMIN = defineModulePermissions({
  id: 'admin',
  name: 'Admin',
  icon: 'shield',
  permissions: {
    view: {
      label: 'View access control',
      description: 'See groups, their members and their permissions.',
    },
    manage: {
      label: 'Manage access control',
      description: 'Create groups, change membership and grant permissions.',
    },
    manageUsers: {
      label: 'Add people',
      description:
        'Create a person’s account before their first sign-in, so they can be put in ' +
        'groups straight away.',
    },
  },
  visibility: 'view',
  /*
   * `manageUsers` is deliberately OFF-LADDER — it is in no level, so it appears in the
   * access editor as a checkbox of its own.
   *
   * Bringing an account into existence is a different kind of act from moving an existing
   * person between groups: it is the one admin action that adds to who can sign in at all.
   * Someone can reasonably be trusted to run access control day to day without that, which
   * is the same argument that split `view` from `manage` in the first place.
   *
   * Administrators still get it with no migration: the seed grants that group every
   * module's top level PLUS its off-ladder permissions, and re-applies on every deploy.
   */
  levels: [
    { key: 'view', label: 'View', grants: ['view'] },
    { key: 'manage', label: 'Manage', grants: ['view', 'manage'] },
  ],
});
